import { t } from "i18next";
import type { ForwardedRef, MutableRefObject } from "react";
import { forwardRef, useCallback, useMemo, useRef } from "react";
import { noop } from "rxjs";
import type { Placements } from "@smartsuite/react-ui/lib";
import {
  Icon,
  SingleSelect,
  Text,
  Title,
  Tooltip,
} from "@smartsuite/react-ui/lib";

import { intercomOpenLimitsArticle } from "../../../services/intercom.service";
import { SpinnerStyle } from "../../../components/Spinner/style";
import {
  StyledBack,
  StyledHeader,
  StyledTitle,
  Underscored,
  StyledFilterDropdown,
  StyledOption,
  StyledSelectOption,
} from "./styles";
import type { Workspace } from "../../../types/workspace";
import { PlanCategory } from "../../../types/workspace";
import { globalDropdownsZIndex } from "@smartsuite/react-ui/lib/constants/zIndex";

interface FilterOption {
  value: string;
  label: string;
}

const placements: Placements = ["top", "bottom"];
const options: FilterOption[] = [
  {
    value: "all",
    label: "All",
  },
  {
    value: "success",
    label: "Ran Successfully",
  },
  {
    value: "failed",
    label: "Failed to Run",
  },
];

interface ActivityHeaderProps {
  filterValue?: string;
  workspace: Workspace;
  backTooltipText: string;
  isLoading?: boolean;
  onGoBack: () => void;
  onChangeFilter?: (value: string) => void;
}

export const ActivityHeader = forwardRef(
  (
    {
      filterValue = "",
      workspace,
      isLoading,
      backTooltipText,
      onGoBack,
      onChangeFilter = noop,
    }: ActivityHeaderProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const refDropdown = useRef<HTMLDivElement | null>(null);

    const selectedValue = useMemo(() => {
      return options.find((option) => option.value === filterValue);
    }, [filterValue]);

    const handleChangeFilter = useCallback((option) => {
      onChangeFilter(option.value);
    }, []);

    return (
      <StyledHeader ref={ref}>
        <Tooltip
          padding={2}
          text={backTooltipText}
          placements={placements}
          target={
            <StyledBack onClick={onGoBack}>
              <Icon
                name="arrow-left-1"
                color="currentColor"
                transition={true}
              />
            </StyledBack>
          }
        />

        <StyledTitle>
          <Title size="s" weight="medium">
            {t("job-list.run-history")}
            {isLoading && <SpinnerStyle />}
          </Title>
          <Text color="secondary">
            {t("job-list.you-have-limit-of-run-history", {
              limit: getHistoryLimit(workspace),
            })}{" "}
            <Underscored onClick={() => intercomOpenLimitsArticle()}>
              {t("general.learn-more")}
            </Underscored>
          </Text>
        </StyledTitle>
        {/* TODO: Remove this condition after dev implementation */}
        {false && (
          <StyledFilterDropdown ref={refDropdown}>
            <SingleSelect
              dropdownClassName="repeating-task-dropdown__select"
              placeholderArrowStyle="filled-dark"
              at="right bottom+3"
              my="right top"
              showClear={false}
              showFilter={false}
              dropdownMinWidth={200}
              targetElementRef={refDropdown as MutableRefObject<HTMLDivElement>}
              zIndex={globalDropdownsZIndex}
              value={selectedValue}
              options={options}
              renderOption={renderOption}
              renderSelectedOption={renderSelectedOption}
              onChange={handleChangeFilter}
            />
          </StyledFilterDropdown>
        )}
      </StyledHeader>
    );
  },
);

ActivityHeader.displayName = "ActivityHeader";

const getHistoryLimit = (workspace: Workspace): string => {
  switch (workspace.plan.category) {
    case PlanCategory.Enterprise:
      return t("job-list.limit-1-year");
    case PlanCategory.Free:
      return t("job-list.limit-2-weeks");
    case PlanCategory.Pro:
      return t("job-list.limit-1-year");
    case PlanCategory.Team:
      return t("job-list.limit-6-months");
    case PlanCategory.Trial:
      return t("job-list.limit-2-weeks");
    default:
      return t("job-list.limit-2-weeks");
  }
};

const renderSelectedOption = (option: FilterOption): React.ReactNode => {
  return (
    <StyledSelectOption>
      <Text size="xs" wrapMode="ellipsis">
        <strong>Status : </strong> {option.label}
      </Text>
    </StyledSelectOption>
  );
};

const renderOption = (option: FilterOption): React.ReactNode => {
  return (
    <StyledOption>
      <Text wrapMode="ellipsis" weight="medium" color="currentColor">
        {option.label}
      </Text>
    </StyledOption>
  );
};
